import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  Avatar,
  Dropdown,
  Menu,
  Modal,
  Pagination,
  Spin,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  MailOutlined,
  MoreOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';

import * as actions from '../../actions/usersActions';
import { RootState } from '../../reducers/store';
import { useClient } from 'src/contexts/Client/Client';
import { filterApiResponse } from './utils';
import { TableUserListType, UserTableDataType } from './types';
import { visibleRoles } from './roleLabels';
import { SortKey } from './Users';
import { USERS_ROUTE } from 'src/routes';

import styles from './Users.module.css';

export const CUSTOMERS_PER_PAGE = 24;

const { confirm } = Modal;

const sortUsers = (
  users: TableUserListType,
  sortKey: SortKey
): TableUserListType => {
  const sorted = [...users];
  switch (sortKey) {
    case 'recent':
      return sorted;
    case 'oldest':
      return sorted.reverse();
    case 'alpha':
      return sorted.sort((a, b) =>
        a.user.name.localeCompare(b.user.name, 'fr', { sensitivity: 'base' })
      );
    default:
      return sorted;
  }
};

const UserCard: React.FC<{
  user: UserTableDataType;
  onDelete: (id: string) => void;
}> = ({ user, onDelete }) => {
  const navigate = useNavigate();
  const client = useClient();
  const [networkName, setNetworkName] = useState<string | null>(null);

  const roles = useMemo(() => visibleRoles(user.roles ?? []), [user.roles]);

  useEffect(() => {
    let cancelled = false;
    client.get(
      `/users/${user.id}/groups?only-child=0&per_page=10`,
      (response: any) => {
        if (cancelled) return;
        const groups = response?.data ?? [];
        const networks = groups.filter((g: any) => g?.type === 'network');
        if (networks.length === 0) {
          setNetworkName(null);
          return;
        }
        if (networks.length === 1) {
          setNetworkName(networks[0].name);
        } else {
          setNetworkName(`${networks.length} réseaux`);
        }
      },
      () => {
        if (cancelled) return;
        setNetworkName(null);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [user.id, client]);

  const copyEmail = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user.email) return;
    navigator.clipboard.writeText(user.email);
    message.success('Email copié');
  };

  const onCardClick = () => navigate(`${USERS_ROUTE}/${user.id}`);

  const overlayMenu = (
    <Menu
      items={[
        {
          key: 'email',
          label: "Copier l'email",
          icon: <MailOutlined />,
          disabled: !user.email,
          onClick: ({ domEvent }: any) => copyEmail(domEvent),
        },
      ]}
    />
  );

  const onDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(user.id);
  };

  return (
    <div className={styles.card} onClick={onCardClick}>
      <div className={styles.cardHeader}>
        <Avatar
          src={user.avatarUrl}
          icon={<UserOutlined />}
          size={36}
          className={styles.cardAvatar}
        />
        <div className={styles.cardIdentity}>
          <Tooltip title={user.name}>
            <span className={styles.cardName}>{user.name}</span>
          </Tooltip>
          <Tooltip title={user.email}>
            <span className={styles.cardEmail}>{user.email || '—'}</span>
          </Tooltip>
        </div>
        <Dropdown
          overlay={overlayMenu}
          trigger={['click']}
          placement="bottomRight"
        >
          <button
            className={styles.cardMenu}
            onClick={e => e.stopPropagation()}
          >
            <MoreOutlined />
          </button>
        </Dropdown>
      </div>

      {networkName && (
        <Tooltip title={networkName}>
          <div className={styles.cardNetwork}>
            <TeamOutlined />
            <span>{networkName}</span>
          </div>
        </Tooltip>
      )}

      <div className={styles.cardRoles}>
        {roles.length > 0 ? (
          roles.map(({ role, config }) => (
            <Tooltip key={role} title={config.description}>
              <Tag color={config.color}>{config.label}</Tag>
            </Tooltip>
          ))
        ) : (
          <Tag color="default">Aucun rôle</Tag>
        )}
      </div>

      <div className={styles.cardFooter}>
        <span className={styles.cardDate}>{user.createdAt}</span>
        <Tooltip title="Supprimer cet utilisateur">
          <button
            id={`deleteButton${user.id}`}
            className={styles.cardDeleteBtn}
            onClick={onDeleteClick}
          >
            <DeleteOutlined />
          </button>
        </Tooltip>
      </div>
    </div>
  );
};

const UserList: React.FC<{
  searchTerm: string;
  roleFilter: string[];
  sortKey: SortKey;
  users: TableUserListType;
  usersTotal: number;
  listUsers: (page: number, text: string, roles?: string[]) => void;
  deleteUser: (
    id: string,
    successCallback: () => void,
    failureCallback: () => void
  ) => void;
}> = ({
  searchTerm,
  roleFilter,
  sortKey,
  users,
  usersTotal,
  listUsers,
  deleteUser,
}) => {
  const [current, setCurrent] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);

  const sortedUsers = useMemo(
    () => sortUsers(users, sortKey),
    [users, sortKey]
  );

  const handleUserDeletion = (userId: string) => {
    confirm({
      title: 'Supprimer cet utilisateur ?',
      icon: <ExclamationCircleOutlined />,
      content:
        "Cette action est irréversible. L'utilisateur et ses données seront supprimés.",
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk() {
        deleteUser(
          userId,
          () => message.success("L'utilisateur a bien été supprimé"),
          () =>
            message.error(
              "Une erreur est survenue lors de la suppression de l'utilisateur"
            )
        );
      },
    });
  };

  useEffect(() => {
    setLoading(true);
    listUsers(current, searchTerm, roleFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, roleFilter, current]);

  useEffect(() => {
    setLoading(false);
  }, [users]);

  useEffect(() => {
    setCurrent(1);
  }, [searchTerm, roleFilter]);

  if (loading && users.length === 0) {
    return (
      <div className={styles.empty}>
        <Spin />
      </div>
    );
  }

  if (!loading && users.length === 0) {
    return (
      <div className={styles.empty}>
        <h3>Aucun utilisateur trouvé</h3>
        <p>Essayez de modifier vos filtres ou votre recherche.</p>
      </div>
    );
  }

  return (
    <>
      <div className={styles.resultCount}>
        <strong>{usersTotal.toLocaleString('fr-FR')}</strong>
        <span>utilisateur{usersTotal > 1 ? 's' : ''}</span>
      </div>

      <div className={styles.grid}>
        {sortedUsers.map(row => (
          <UserCard
            key={row.key}
            user={row.user}
            onDelete={handleUserDeletion}
          />
        ))}
      </div>

      <div className={styles.paginationWrapper}>
        <Pagination
          current={current}
          total={usersTotal}
          pageSize={CUSTOMERS_PER_PAGE}
          showSizeChanger={false}
          onChange={page => setCurrent(page)}
        />
      </div>
    </>
  );
};

const mapStateToProps = ({ users }: RootState) => {
  return {
    users: filterApiResponse(users.items),
    usersTotal: users.total,
  };
};

export default connect(mapStateToProps, actions)(UserList);
